import type { ChangeEvent, ForwardedRef } from 'react';
import React, { forwardRef, useImperativeHandle, useMemo, useRef } from 'react';
import { useTranslation } from 'next-i18next';

import type { BoxProps, PaperProps, PopperProps } from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import MuiAvatar from '@mui/material/Avatar';

import Tag from 'src/components/ui/Tag';
import Loader from 'src/components/ui/Loader';
import { StyledTextField } from 'src/components/ui/TextField';
import autocompleteInputStyles from 'src/constants/autocompleteInputStyles';
import SimpleHorizontalUserCard from 'src/components/ui/SimpleHorizontalUserCard';
import Icon from 'src/components/ui/Icon';
import Avatar from 'src/components/ui/Avatar';
import EmptyState from 'src/components/ui/EmptyState';

import type { User, Medium } from 'src/types/__generated__/graphql';

import theme from 'src/utils/theme';
import { colours } from 'src/constants/colours';
import { breakpoints } from 'src/constants/breakpoints';
import { styledComponent, styledTag } from 'src/utils/styled';
import SimpleHorizontalCard from 'src/components/ui/SimpleHorizontalCard';

const StyledAutocomplete = styledComponent(Autocomplete<Option, true, true, false>)`
    ${autocompleteInputStyles}
    .MuiInputBase-root {
        min-height: 3.25rem;
    }
    .MuiTextField-root {
        margin: 0;
    }
    .MuiFilledInput-root.MuiInputBase-sizeSmall {
        padding: 0.5rem;
    }
    .MuiAutocomplete-listbox[class*='MuiFilledInput-listbox'] {
        padding-left: 0rem;
    }
    .MuiAutocomplete-endAdornment {
        bottom: 0.5rem;
    }
`;

const StyledTextfieldContainer = styledTag('div')<{ $maxHeight?: string }>`
    ${({ $maxHeight }) => `max-height: ${$maxHeight ?? '7rem'} !important;`};
    min-height: 3.25rem;
    overflow-y: scroll;
    border-radius: 0.25rem;
    border: 0.0625rem solid ${colours.primary.O08};
    :has(.MuiInputBase-root.Mui-focused) {
        border-color: ${colours.primary.O60};
    }
    .MuiInputBase-root,
    .MuiFilledInput-root.MuiInputBase-sizeSmall {
        border: none;
    }
`;

const StyledStartIcon = styledComponent(Box)`
    @media (max-width: ${breakpoints.tabletPortrait - 1}px) {
        display: flex;
        flex-direction: row;
        align-items: center;
    }
`;

const StyledCloseIcon = styledTag('div')`
    display: flex;
    justify-content: center;
    align-items: center;
    color: ${theme.palette.primary.main};
    svg {
        cursor: pointer !important;
    }
`;

const StyledPopper = styledComponent(Box)<{ $fixedHeight?: string }>`
    margin-top: 1rem;
    display: flex;
    flex-direction: column;
    flex: 1;
    ${({ $fixedHeight }) => $fixedHeight && `height: ${$fixedHeight};`}
    & .MuiAutocomplete-option {
        padding: 0rem !important;
        margin: 0.375rem 0rem 0.375rem 0rem !important;
    }
    & .MuiAutocomplete-option[aria-disabled='true'] {
        opacity: 1 !important;
    }
    ul {
        padding-bottom: 0;
        padding-top: 0;
    }
`;

export type Option =
    | {
          type: 'user';
          value: User;
      }
    | {
          type: 'email';
          value: string;
      };

export interface Props {
    name: string;
    onChangeTextField?: React.ChangeEventHandler<HTMLInputElement | HTMLTextAreaElement> | undefined;
    onChangeSelectedUsers?: (_event: ChangeEvent<{ value?: string }>, items?: Option[]) => void;
    showLabel?: boolean;
    options?: Option[];
    loading?: boolean;
    textFieldMaxHeight?: string;
    emptyStateIconSize?: string;
    fixedHeight?: string;
    optionCardRightArea: (option: Option, selected: boolean) => React.ReactElement;
    optionDisabled: (option: Option) => boolean;
}

export const MultipleUserPicker: React.FC<Props> = ({
    name,
    onChangeTextField,
    onChangeSelectedUsers,
    showLabel,
    options,
    loading,
    textFieldMaxHeight,
    emptyStateIconSize,
    fixedHeight,
    optionCardRightArea,
    optionDisabled,
}) => {
    const { t } = useTranslation(['common', 'contacts']);
    const textFieldContainerRef = useRef<HTMLDivElement | null>(null);

    const CustomPaper = useMemo(() => (paperProps: PaperProps) => paperProps.children as React.ReactElement, []);

    const CustomPopper = useMemo(
        () => (popperProps: PopperProps) =>
            (
                <StyledPopper $fixedHeight={fixedHeight}>{popperProps.children as React.ReactNode}</StyledPopper>
            ) as React.ReactElement,
        [fixedHeight],
    );

    const handleChangeAutocomplete = (_event: ChangeEvent<{ value?: string }>, items?: Option[]) => {
        onChangeSelectedUsers?.(_event, items);
        setTimeout(() => {
            if (textFieldContainerRef.current) {
                textFieldContainerRef.current.scrollTop = textFieldContainerRef.current.scrollHeight;
            }
        });
    };

    return (
        <StyledAutocomplete
            data-target-id="multiple-user-picker"
            id="tags-filled"
            options={[...(options ?? [])]}
            onChange={handleChangeAutocomplete}
            filterOptions={(opts) => opts}
            getOptionLabel={(opt) => (opt.type === 'user' ? opt.value.id : opt.value)}
            isOptionEqualToValue={(option, value) => {
                if (option.type !== value.type) {
                    return false;
                }
                if (option.type === 'email' && value.type === 'email') {
                    return option.value === value.value;
                }
                return (option.value as User).id === (value.value as User).id;
            }}
            renderTags={(values, getTagProps) =>
                values.map((item, index) => {
                    const key = item.type === 'user' ? item.value.id : item.value;
                    const tagLabel = item.type === 'user' ? item.value.fullName : item.value;
                    const avatarImage = item.type === 'user' ? (item.value.image as Medium) : undefined;
                    const avatarFallbackText =
                        item.type === 'user'
                            ? item.value.initials ?? item.value.fallbackInititals
                            : item.value.slice(0, 1);
                    return (
                        <span key={key}>
                            <Tag
                                data-target="user-selected"
                                data-target-id={`user-selected-${index}`}
                                label={tagLabel}
                                {...getTagProps({ index })}
                                size="small"
                                style={{ backgroundColor: colours.secondary.O08 }}
                                avatar={
                                    item.type === 'user' ? (
                                        <Avatar
                                            alt={tagLabel}
                                            image={avatarImage}
                                            fallbackText={avatarFallbackText}
                                            size={24}
                                            style={{ marginLeft: 0, color: colours.lightBackgroundGray }}
                                        />
                                    ) : (
                                        <MuiAvatar style={{ marginLeft: 0 }}>
                                            <Icon name="Mail" color={colours.lightBackgroundGray} size="16px" />
                                        </MuiAvatar>
                                    )
                                }
                                deleteIcon={
                                    <StyledCloseIcon>
                                        <Icon name="Close" size="1rem" />
                                    </StyledCloseIcon>
                                }
                            />
                        </span>
                    );
                })
            }
            PaperComponent={CustomPaper}
            PopperComponent={CustomPopper}
            getOptionDisabled={optionDisabled}
            renderOption={(props: unknown, option, { selected }) => {
                const key = option.type === 'user' ? option.value.id : option.value;
                return (
                    <Box
                        key={key}
                        onClick={(e) => e.stopPropagation()}
                        {...(props as BoxProps)}
                        data-target="autocomplete-option"
                        data-target-id={`autocomplete-option-${key}`}
                    >
                        <Box width="100%">
                            {option.type === 'user' ? (
                                <SimpleHorizontalUserCard
                                    user={option.value}
                                    padding="0.75rem 1rem"
                                    onClick={() => null}
                                    rightArea={optionCardRightArea(option, selected)}
                                />
                            ) : (
                                <EmailCard
                                    email={option.value}
                                    selected={selected}
                                    optionCardRightArea={optionCardRightArea}
                                />
                            )}
                        </Box>
                    </Box>
                );
            }}
            noOptionsText={
                <Box margin="auto" height={fixedHeight} display="flex" alignItems="center">
                    {loading ? (
                        <Loader />
                    ) : (
                        <EmptyState
                            icon="SearchPerson"
                            variant1="subtitle1"
                            text1={t('contacts:noProfilesFound')}
                            iconSize={emptyStateIconSize}
                        />
                    )}
                </Box>
            }
            popupIcon={false}
            renderInput={(params) => (
                <StyledTextfieldContainer ref={textFieldContainerRef} $maxHeight={textFieldMaxHeight}>
                    <StyledTextField
                        {...params}
                        name={`${name}-search`}
                        onChange={onChangeTextField}
                        size="small"
                        placeholder={showLabel ? t('common:search') : undefined}
                        InputProps={{
                            ...params.InputProps,
                            disableUnderline: true,
                            startAdornment: (
                                <>
                                    {showLabel && (
                                        <StyledStartIcon>
                                            <Icon name="Search" color="primary" />
                                        </StyledStartIcon>
                                    )}
                                    {params.InputProps.startAdornment}
                                </>
                            ),
                            endAdornment: params.InputProps.endAdornment,
                        }}
                        fullWidth
                    />
                </StyledTextfieldContainer>
            )}
            ListboxComponent={ListBox}
            open
            multiple
            disableCloseOnSelect
            disableClearable
        />
    );
};

type NullableUlElement = HTMLUListElement | null;
interface ListBoxProps extends React.HTMLAttributes<HTMLUListElement> {}

// This component should let us do infinite scroll in an autocomplete, see bellow links
// https://stackoverflow.com/questions/66428175/material-ui-autocomplete-infinite-scrolling-together
// https://github.com/mui/material-ui/issues/30249
// https://github.com/mui/material-ui/issues/29508
const ListBox = forwardRef((props: ListBoxProps, ref: ForwardedRef<HTMLUListElement>) => {
    const { children, ...rest } = props;

    const innerRef = useRef<HTMLUListElement>(null);

    useImperativeHandle<NullableUlElement, NullableUlElement>(ref, () => innerRef.current);

    return (
        <ul
            {...rest}
            ref={innerRef}
            // eslint-disable-next-line jsx-a11y/aria-role
            role="list-box"
        >
            {children}
        </ul>
    );
});

type EmailCardProps = {
    email: string;
    selected: boolean;
    optionCardRightArea: (option: Option, selected: boolean) => React.ReactElement;
};
const EmailCard: React.FC<EmailCardProps> = ({ email, selected, optionCardRightArea }) => (
    <SimpleHorizontalCard
        keyIdentifier={email}
        padding="0.75rem 1rem"
        onClick={() => null}
        rightArea={optionCardRightArea({ type: 'email', value: email }, selected)}
        avatar={
            <MuiAvatar>
                <Icon name="Mail" color={colours.lightBackgroundGray} />
            </MuiAvatar>
        }
        name={email}
        cursorPointer={false}
        disableRipple
    />
);
